import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck, faFilePdf, faMapPin, faCircleCheck, faBolt,
} from '@fortawesome/free-solid-svg-icons';
import './acts.css';
import './Act1_Onboard.css';

// Pre-staged pin coordinates on the mock floor plan (percentages)
const PINS = [
  { x: 18, y: 22, id: 'UCL44-AS-001' },
  { x: 28, y: 38, id: 'UCL44-AS-002' },
  { x: 44, y: 28, id: 'UCL44-AS-003' },
  { x: 58, y: 48, id: 'UCL44-AS-004' },
  { x: 72, y: 32, id: 'UCL44-AS-005' },
  { x: 84, y: 58, id: 'UCL44-AS-006' },
  { x: 34, y: 66, id: 'UCL44-AS-007' },
  { x: 52, y: 72, id: 'UCL44-AS-008' },
  { x: 68, y: 76, id: 'UCL44-AS-009' },
  { x: 22, y: 82, id: 'UCL44-AS-010' },
];

export default function Act1Onboard({ active }) {
  const [phase, setPhase] = useState(0); // 0=upload, 1=dropping, 2=ids generated
  const timers = useRef([]);

  useEffect(() => {
    if (!active) return;
    // reset
    setPhase(0);
    timers.current.forEach(clearTimeout);
    timers.current = [];
    // phase 1 after 800ms
    timers.current.push(setTimeout(() => setPhase(1), 800));
    // phase 2 after pins all dropped (~ 800 + pin count * 120 + 400)
    timers.current.push(setTimeout(() => setPhase(2), 800 + PINS.length * 120 + 500));
    return () => { timers.current.forEach(clearTimeout); timers.current = []; };
  }, [active]);

  const visiblePinCount = phase >= 1
    ? (phase >= 2 ? PINS.length : Math.min(PINS.length, Math.max(0, PINS.length)))
    : 0;

  return (
    <div className="act act-1">
      {/* ── Narrative ── */}
      <div className="act-copy">
        <span className="act-eyebrow">
          <span className="act-eyebrow-dot" /> Act 1 · Onboarding
        </span>
        <h1 className="act-title">
          Any building, onboarded in <span className="act-title-accent">minutes</span>.
        </h1>
        <p className="act-lede">
          Upload a floor-plan PDF, drop a pin for each asset, and CloudSafe generates
          unique IDs across the whole property — with the right category codes
          baked in automatically.
        </p>
        <ul className="act-proofs">
          <li className="act-proof">
            <span className="act-proof-check"><FontAwesomeIcon icon={faCheck} /></span>
            <span><strong>One PDF in, hundreds of assets out.</strong> No spreadsheets, no copy-paste.</span>
          </li>
          <li className="act-proof">
            <span className="act-proof-check"><FontAwesomeIcon icon={faCheck} /></span>
            <span><strong>Smart IDs.</strong> <code>UCL44-AS-001</code> encodes property code, category, and sequence.</span>
          </li>
          <li className="act-proof">
            <span className="act-proof-check"><FontAwesomeIcon icon={faCheck} /></span>
            <span><strong>Every pin becomes an asset</strong> with its own test history and drawing location.</span>
          </li>
        </ul>
        <div className="act-stats">
          <div className="act-stat"><span className="act-stat-val">198</span><span className="act-stat-lbl">Assets · UCL East</span></div>
          <div className="act-stat"><span className="act-stat-val">1</span><span className="act-stat-lbl">PDF upload</span></div>
          <div className="act-stat"><span className="act-stat-val">&lt;10m</span><span className="act-stat-lbl">Time to live</span></div>
        </div>
      </div>

      {/* ── Stage ── */}
      <div className="act-stage">
        <div className="act-device a1-device">
          <div className="act-device-chrome">
            <div className="act-device-dots"><span /><span /><span /></div>
            <div className="act-device-addr">cloudsafe.uk/cloud-staff/asset-generation-v2</div>
          </div>

          <div className="a1-canvas">
            {/* Floor plan */}
            <svg viewBox="0 0 800 500" className="a1-plan" preserveAspectRatio="xMidYMid meet">
              <defs>
                <pattern id="a1-grid" width="20" height="20" patternUnits="userSpaceOnUse">
                  <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#e9edf3" strokeWidth="1" />
                </pattern>
              </defs>
              <rect width="800" height="500" fill="#fafbfd" />
              <rect width="800" height="500" fill="url(#a1-grid)" />
              {/* Outer walls */}
              <rect x="40" y="40" width="720" height="420" fill="none" stroke="#0c437c" strokeWidth="3" rx="6" />
              {/* Inner walls */}
              <line x1="40" y1="180" x2="380" y2="180" stroke="#0c437c" strokeWidth="2" />
              <line x1="380" y1="40" x2="380" y2="280" stroke="#0c437c" strokeWidth="2" />
              <line x1="380" y1="280" x2="760" y2="280" stroke="#0c437c" strokeWidth="2" />
              <line x1="560" y1="280" x2="560" y2="460" stroke="#0c437c" strokeWidth="2" />
              <line x1="200" y1="280" x2="200" y2="460" stroke="#0c437c" strokeWidth="2" />
              {/* Rooms labels */}
              <text x="120" y="110" fill="#94a3b8" fontSize="12" fontFamily="Inter" fontWeight="500">ROOF · NORTH</text>
              <text x="500" y="110" fill="#94a3b8" fontSize="12" fontFamily="Inter" fontWeight="500">ROOF · EAST</text>
              <text x="480" y="230" fill="#94a3b8" fontSize="12" fontFamily="Inter" fontWeight="500">PLANT DECK</text>
              <text x="110" y="390" fill="#94a3b8" fontSize="12" fontFamily="Inter" fontWeight="500">MEZZ · A</text>
              <text x="340" y="390" fill="#94a3b8" fontSize="12" fontFamily="Inter" fontWeight="500">MEZZ · B</text>
              <text x="630" y="390" fill="#94a3b8" fontSize="12" fontFamily="Inter" fontWeight="500">ROOFTOP PLANT</text>

              {/* Pins */}
              {PINS.map((p, i) => (
                <g
                  key={p.id}
                  className={`a1-pin ${phase >= 1 ? 'is-in' : ''}`}
                  style={{ animationDelay: `${i * 120}ms` }}
                  transform={`translate(${p.x * 8}, ${p.y * 5})`}
                >
                  <circle r="18" fill="rgba(12, 67, 124, 0.14)" className="a1-pin-ring" />
                  <circle r="10" fill="#0c437c" />
                  <circle r="4" fill="#fff" />
                </g>
              ))}
            </svg>

            {/* Floating upload chip before pins drop */}
            <div className={`a1-upload ${phase >= 1 ? 'is-gone' : ''}`}>
              <FontAwesomeIcon icon={faFilePdf} className="a1-upload-ico" />
              <div>
                <div className="a1-upload-name">UCL_East_Roof_Plan.pdf</div>
                <div className="a1-upload-meta">2.4 MB · uploaded</div>
              </div>
              <FontAwesomeIcon icon={faCircleCheck} className="a1-upload-ok" />
            </div>
          </div>

          {/* Sidebar: generated IDs */}
          <div className="a1-sidebar">
            <div className="a1-sidebar-head">
              <FontAwesomeIcon icon={faMapPin} />
              <span>Generated asset IDs</span>
              <span className="a1-sidebar-count">{phase >= 2 ? PINS.length : phase === 1 ? '…' : 0}</span>
            </div>
            <ul className="a1-id-list">
              {PINS.map((p, i) => (
                <li
                  key={p.id}
                  className={`a1-id ${phase >= 2 ? 'is-in' : ''}`}
                  style={{ animationDelay: `${i * 60 + 200}ms` }}
                >
                  <span className="a1-id-num">{String(i + 1).padStart(2, '0')}</span>
                  <code className="a1-id-code">{p.id}</code>
                  <span className="a1-id-tag">Abseil System</span>
                </li>
              ))}
            </ul>
            {phase >= 2 && (
              <div className="a1-done">
                <FontAwesomeIcon icon={faBolt} />
                <span>All {PINS.length} assets created and pinned to drawing.</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
